import {
  Alert,
  AlertIcon,
  Box,
  Icon,
  Spinner,
  useToast
} from "@chakra-ui/react";
import React, {
  useEffect,
  useRef,
  useState
} from "react";
import { useParams } from "react-router-dom";
import {
  isWithinInterval,
  isAfter,
  isBefore,
  format,
  addSeconds
} from "date-fns";
import { FaHeadphones } from "react-icons/fa";
import {
  Auth,
  Storage
} from "aws-amplify";

import {
  ConversationMode,
  Message,
  useConversationsApi,
  useTopicsApi
} from "src/api";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { useAuthContext } from "src/hooks/useAuthContext";
import {
  AudioMedia,
  useAudioMediaApi
} from "src/api/audio-media";

import { Chat } from "./components/Chat";
import { VoiceChat } from "./components/VoiceChat";
import { Messages } from "./components/Messages";
import { VoicePlayback } from "./components/VoicePlayback";

interface PageParams {
  conversationId: string;
  topicId: string;
}

export const ConversationTopicPage: React.FC = () => {
  const toast = useToast();
  const { conversationId, topicId } = useParams<PageParams>();
  const [ conversationRes, conversationReq ] = useConversationsApi("GET_CONVERSATION");
  const [ topicRes, topicReq ] = useTopicsApi("GET_TOPIC");
  const [ createAudioMediaRes, createAudioMediaReq ] = useAudioMediaApi("CREATE_AUDIO_MEDIA");
  const [ messages, setMessages ] = useState<Message[]>([]);
  const [ ready, setReady ] = useState(false);
  const [ audioMedia, setAudioMedia ] = useState<AudioMedia[]>([]);
  const [ isSessionActive, setIsSessionActive ] = useState(false);
  const [ isSessionOver, setIsSessionOver ] = useState(false);
  const [ sessionNotStarted, setSessionNotStarted ] = useState(false);
  const { authDataState } = useAuthContext();
  const [ serverTime, setServerTime ] = useState<Date | null>();
  // Media recorder refs for voice chat
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const recordingStartTime = useRef<Date | null>(null);
  const isRecording = useRef(false);
  const hasRecordedThisVisit = useRef(false);
  const hasAttemptedUploadThisVisit = useRef(false);
  const [ mediaStream, setMediaStream ] = useState<MediaStream | null>(null);

  // Perform initial requests
  useEffect(() => {
    // Get the conversation
    conversationReq({ id: conversationId });
    // Get the topic, along with all messages
    topicReq({ id: topicId });
  }, [
    conversationReq,
    conversationId,
    topicId,
    topicReq
  ]);

  // Set topic response
  useEffect(() => {
    if (topicRes.data) {
      setMessages(topicRes.data.messages || []);
      setAudioMedia(topicRes.data.audioMedia || []);
    }
  }, [ topicRes.data ]);

  // Set conversation response
  useEffect(() => {
    if (conversationRes.data?.mode === ConversationMode.VOICE) {
      // Ask permission to use microphone:
      navigator.mediaDevices.getUserMedia({ audio: true });
    }
  }, [ conversationRes.data ]);

  const fetched = useRef(false);

  // Handle session when it's OVER
  useEffect(() => {
    if (isSessionOver && !fetched.current) {
      if (conversationRes.data?.mode === ConversationMode.MESSAGE && messages.length === 0) {
        fetched.current = true;
        topicReq({ id: topicId });
      }

      if (conversationRes.data?.mode === ConversationMode.VOICE && audioMedia.length === 0) {
        fetched.current = true;
        topicReq({ id: topicId });
      }
    }
  }, [
    topicId,
    topicReq,
    isSessionOver,
    conversationRes.data,
    messages,
    audioMedia
  ]);

  // Run every second, on server time
  useEffect(() => {
    const interval = setInterval(() => {
      if (conversationRes.serverTime) {
        const serverTime = new Date(conversationRes.serverTime);

        setServerTime(currentTime => currentTime ? addSeconds(currentTime, 1) : serverTime);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [ conversationRes.serverTime ]);

  // Checks status of conversation based on server time
  useEffect(() => {
    if (conversationRes.data && serverTime) {
      const isOver = isAfter(serverTime, new Date(conversationRes.data.endTime));
      const hasNotStarted = isBefore(serverTime, new Date(conversationRes.data.startTime));

      const isActive = isWithinInterval(serverTime, {
        start: new Date(conversationRes.data.startTime),
        end: new Date(conversationRes.data.endTime)
      });

      setIsSessionOver(isOver);
      setIsSessionActive(isActive);
      setSessionNotStarted(hasNotStarted);
      setReady(true);
    }
  }, [ serverTime, conversationRes.data ]);

  // Setup MediaRecorder when session is active and mode is voice
  useEffect(() => {
    if (!isSessionActive || conversationRes.data?.mode !== ConversationMode.VOICE || !authDataState.user) {
      return;
    }

    // Request microphone access and setup recording
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      // Mark that we've started recording
      recordingStartTime.current = new Date();
      isRecording.current = true;
      hasRecordedThisVisit.current = true;
      // Store the stream for VoiceChat to use
      setMediaStream(stream);
      // Setup the mediaRecorder
      const recorder = new MediaRecorder(stream);

      mediaRecorder.current = recorder;

      recorder.ondataavailable = blobEvent => {
        chunks.current.push(blobEvent.data);
      };

      recorder.onstop = async () => {
        try {
          // Create the Blob from the chunks
          const audioBlob = new Blob(chunks.current, { type: "audio/mp4" });

          if (authDataState.user?._id && topicRes.data && !hasAttemptedUploadThisVisit.current) {
            hasAttemptedUploadThisVisit.current = true;

            const result = await Storage.put(`${topicRes.data._id}.mp4`, audioBlob, {
              level: "protected",
              contentType: "audio/mp4"
            });

            if (!result) {
              throw new Error("Upload did not complete");
            }

            const user = await Auth.currentCredentials();
            const fileUrl = `/protected/${user.identityId}/${result.key}`;

            // Create audio media record
            createAudioMediaReq({
              fileUrl,
              userId: authDataState.user._id,
              topicId: topicRes.data._id,
              startTime: recordingStartTime.current || new Date(),
              endTime: new Date()
            });
          }
        } catch (error) {
          toast({
            status: "error",
            title: "Failed to store your audio for later playback"
          });
        }

        // Reset recording flag (but keep hasRecordedThisVisit true)
        mediaRecorder.current = null;
        chunks.current = [];
        isRecording.current = false;
        // Stop the stream tracks
        stream.getTracks().forEach(track => track.stop());
      };
      recorder.start(3000);
    }).catch(error => {
      console.error("Error setting up audio:", error);

      toast({
        status: "error",
        title: "There was a problem setting up your audio."
      });
    });

    // Cleanup function
    return () => {
      if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
        mediaRecorder.current.stop();
      }

      // Clear the stream when cleaning up
      setMediaStream(null);
    };
  }, [
    isSessionActive,
    conversationRes.data?.mode,
    authDataState.user,
    topicRes.data,
    createAudioMediaReq,
    toast
  ]);

  // Handle media recorder upload when session ends
  useEffect(() => {
    if (isSessionOver && isRecording.current && mediaRecorder.current) {
      // Stop recording if still active
      if (mediaRecorder.current.state === "recording") {
        mediaRecorder.current.stop();
      }
    }
  }, [ isSessionOver ]);

  // Handle audio media upload errors
  useEffect(() => {
    if (createAudioMediaRes.error) {
      console.error("Error uploading audio", createAudioMediaRes.error);

      toast({
        status: "error",
        title: "There was an error uploading your recording"
      });
    }
  }, [ createAudioMediaRes.error, toast ]);

  // Refetch topic when audio upload completes successfully
  useEffect(() => {
    if (createAudioMediaRes.complete && !createAudioMediaRes.error) {
      setTimeout(() => {
        // Refetch to get the updated audioMedia
        topicReq({ id: topicId });
      }, 2000);
    }
  }, [
    createAudioMediaRes.complete,
    createAudioMediaRes.error,
    topicReq,
    topicId
  ]);

  const UploadStatusBanner: React.FC = () => {
    // Only show if we recorded during this visit and session is over
    if (!hasRecordedThisVisit.current || !isSessionOver) {
      return null;
    }

    if (createAudioMediaRes.pending) {
      return (
        <Box
          mt={4}
          p={3}
          borderRadius={8}
          bg="#f7fafc"
        >
          <Alert
            status="info"
            variant="subtle"
          >
            <AlertIcon />
            Uploading your audio...
          </Alert>

        </Box>
      );
    }

    if (createAudioMediaRes.complete) {
      return (
        <Box
          mt={4}
          p={3}
          borderRadius={8}
          bg="#f7fafc"
        >
          <Alert
            status="success"
            variant="subtle"
          >
            <AlertIcon />
            Your audio was uploaded successfully.
          </Alert>
        </Box>
      );
    }

    if (createAudioMediaRes.error) {
      return (
        <Box
          mt={4}
          p={3}
          borderRadius={8}
          bg="#fef2f2"
        >
          <Alert
            status="error"
            variant="subtle"
          >
            <AlertIcon />
            There was a problem uploading your audio.
          </Alert>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      p={10}
      height={!isSessionActive ? "calc(100vh - 90px)" : "100vh"}
    >
      {!isSessionActive && <Header />}

      {(conversationRes.error || topicRes.error) && !conversationRes.data ? (
        <Text as="p">
          {conversationRes.error?.details || topicRes.error?.details || "You are not allowed to access this session"}
        </Text>
      ) : (conversationRes.pending || !ready) ? (
        <Spinner />
      ) : (
        <>

          {(conversationRes.data && topicRes.data) ? (
            <Box
              display="flex"
              flexDirection="column"
              height="100%"
            >
              <Box>
                {conversationRes.data.participants?.length && (
                  <Text
                    as='h3'
                    bold
                  >
                    {`Session with ${conversationRes.data.participants.map(p => p.user.name).join(" and ")}`}
                  </Text>
                )}

                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                >
                  <Text as="p">
                    {`${format(new Date(conversationRes.data.date), "EEEE do MMMM")} | ${format(new Date(conversationRes.data.startTime), "HH:mm")} - ${format(new Date(conversationRes.data.endTime), "HH:mm")}`}
                  </Text>

                  {isSessionActive && conversationRes.data.mode === "voice" && (

                    <Icon
                      as={FaHeadphones}
                      w={5}
                      h={5}
                      ml="0.5rem"
                      title="Please use headphones"
                    />

                  )}
                </Box>

                {isSessionOver && (
                  <Box paddingTop="1rem">
                    <Text as="p">
                      This session has finished.
                    </Text>
                  </Box>
                )}

              </Box>

              {conversationRes.data.mode === "voice" && isSessionOver && (
                <UploadStatusBanner />
              )}

              {(isSessionActive) && authDataState.user && (
                <>
                  {conversationRes.data.mode === "voice" ? (
                    <VoiceChat
                      conversation={conversationRes.data}
                      topic={topicRes.data}
                      userId={authDataState.user?._id}
                      isSessionOver={isSessionOver}
                      mediaStream={mediaStream}
                    />
                  ) : (
                    <Chat
                      conversation={conversationRes.data}
                      topic={topicRes.data}
                      userId={authDataState.user._id}
                    />
                  )}
                </>
              )}

              {isSessionOver && authDataState.user && (
                <>
                  {conversationRes.data.mode === "voice" ? (
                    <VoicePlayback
                      audioMedia={audioMedia}
                      conversation={conversationRes.data}
                    />
                  ) : (
                    <Messages
                      messages={messages}
                      conversation={conversationRes.data}
                    />
                  )}
                </>
              )}

              {sessionNotStarted && (
                <Box>
                  <Text as="p">
                    This conversation hasn't started yet.
                  </Text>

                  {conversationRes.data.mode === "voice" && (
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      paddingTop={10}
                    >
                      <Icon
                        as={FaHeadphones}
                        w={5}
                        h={5}
                        mr="0.5rem"
                      />

                      <Text as="p">
                        For the best experience, please use headphones.
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Spinner />
          )}
        </>
      )}

    </Box>
  );
};
